// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { isUndefined } from '@shared/utils';
import inversisOptions from '../utils/inversis-options.json';

const invertOptionsMap = (options: Map<string, string>): Map<string, string> =>
  new Map(Array.from(options, ([slug, id]) => [id, slug]));

export const INVERSIS_COUNTRY_OPTIONS = new Map(
  Object.entries(inversisOptions.countries),
);

export const INVERSIS_ACTIVITY_OPTIONS = new Map(
  Object.entries(inversisOptions.activities),
);

export const INVERSIS_DOCUMENT_TYPE_OPTIONS = new Map(
  Object.entries(inversisOptions.documentTypes),
);

export const INVERSIS_SECTOR_OPTIONS = new Map(
  Object.entries(inversisOptions.sectors),
);

export const INVERSIS_VIA_OPTIONS = new Map(Object.entries(inversisOptions.vias));

export const INVERSIS_PROVINCE_OPTIONS = new Map(
  Object.entries(inversisOptions.provinces),
);

export const INVERSIS_EMPLOYMENT_STATUS_OPTIONS = new Map(
  Object.entries(inversisOptions.employmentStatus),
);

export const INVERSIS_KYC_QUESTION_OPTIONS = new Map(
  Object.entries(inversisOptions.kycQuestions),
);

export const INVERSIS_KYC_ANSWER_OPTIONS = new Map(
  Object.entries(inversisOptions.kycAnswers),
);

export const INVERSIS_IDS_OPTIONS = new Map([
  ...INVERSIS_COUNTRY_OPTIONS,
  ...INVERSIS_ACTIVITY_OPTIONS,
  ...INVERSIS_DOCUMENT_TYPE_OPTIONS,
  ...INVERSIS_SECTOR_OPTIONS,
  ...INVERSIS_VIA_OPTIONS,
  ...INVERSIS_PROVINCE_OPTIONS,
  ...INVERSIS_EMPLOYMENT_STATUS_OPTIONS,
  ...INVERSIS_KYC_QUESTION_OPTIONS,
  ...INVERSIS_KYC_ANSWER_OPTIONS,
]);

const INVERSIS_IDS_OPTIONS_MAP = {
  countries: INVERSIS_COUNTRY_OPTIONS,
  activities: INVERSIS_ACTIVITY_OPTIONS,
  documentTypes: INVERSIS_DOCUMENT_TYPE_OPTIONS,
  sectors: INVERSIS_SECTOR_OPTIONS,
  vias: INVERSIS_VIA_OPTIONS,
  provinces: INVERSIS_PROVINCE_OPTIONS,
  employmentStatus: INVERSIS_EMPLOYMENT_STATUS_OPTIONS,
  kycQuestions: INVERSIS_KYC_QUESTION_OPTIONS,
  kycAnswers: INVERSIS_KYC_ANSWER_OPTIONS,
  all: INVERSIS_IDS_OPTIONS,
};

const INVERSIS_IDS_BY_ID_MAP = {
  countries: invertOptionsMap(INVERSIS_COUNTRY_OPTIONS),
  activities: invertOptionsMap(INVERSIS_ACTIVITY_OPTIONS),
  documentTypes: invertOptionsMap(INVERSIS_DOCUMENT_TYPE_OPTIONS),
  sectors: invertOptionsMap(INVERSIS_SECTOR_OPTIONS),
  vias: invertOptionsMap(INVERSIS_VIA_OPTIONS),
  provinces: invertOptionsMap(INVERSIS_PROVINCE_OPTIONS),
  employmentStatus: invertOptionsMap(INVERSIS_EMPLOYMENT_STATUS_OPTIONS),
  kycQuestions: invertOptionsMap(INVERSIS_KYC_QUESTION_OPTIONS),
  kycAnswers: invertOptionsMap(INVERSIS_KYC_ANSWER_OPTIONS),
  all: invertOptionsMap(INVERSIS_IDS_OPTIONS),
};

export const getInversisOptionId = (
  slug: string,
  namespace: keyof typeof INVERSIS_IDS_OPTIONS_MAP = 'all',
): string => {
  const options = INVERSIS_IDS_OPTIONS_MAP[namespace];

  if (isUndefined(options)) throw new RangeError(`Invalid namespace: ${namespace}`);

  if (!options.has(slug))
    throw new RangeError(`Invalid slug for namespace: ${namespace}: ${slug}`);

  return options.get(slug)!;
};

export const getInversisOptionSlug = (
  id: string,
  namespace: keyof typeof INVERSIS_IDS_BY_ID_MAP = 'all',
): string | undefined => {
  const options = INVERSIS_IDS_BY_ID_MAP[namespace];

  if (isUndefined(options)) {
    throw new RangeError(`Invalid namespace: ${namespace}`);
  }

  return options.get(id);
};
